<script>
    import { onMount, onDestroy } from "svelte";

    import Quill from "quill";
    import "quill/dist/quill.snow.css";

    export let content = "";
    export let config = {};

    let editorContainer, editor, quillInstance;

    const defaultConfig = {
        showToolbar: true,
        toolbarOptions: [
            [{ font: [] }],
            [{ size: ["small", "medium", "large", "huge"] }],
            ["bold", "italic", "underline"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["blockquote"],
            [{ align: [] }],
            [{ script: "sub" }, { script: "super" }],
            [{ indent: "-1" }, { indent: "+1" }],
            ["clean"],
        ],
        placeholder: "Type something...",
        // ✅ NEW: force paste to plain text
        pasteAsPlainText: true,
    };

    $: runningConfig = { ...defaultConfig, ...config };
    $: if (quillInstance) syncEditorStyle(runningConfig);

    let removePasteListener = null;

    const installPlainPasteHandler = () => {
        if (!quillInstance) return;

        const handler = (e) => {
            if (!runningConfig?.pasteAsPlainText) return;

            // Only handle real clipboard paste events
            if (!e.clipboardData) return;

            e.preventDefault();
            e.stopPropagation();

            const text = (e.clipboardData.getData("text/plain") || "").replace(/\u00A0/g, " ");
            if (text.length === 0) return;

            // Ensure we have a selection
            const range = quillInstance.getSelection(true) || {
                index: quillInstance.getLength(),
                length: 0,
            };

            // Replace selection with plain text
            if (range.length) {
                quillInstance.deleteText(range.index, range.length, "user");
            }

            quillInstance.insertText(range.index, text, "user");
            quillInstance.setSelection(range.index + text.length, 0, "silent");
        };

        // Capture phase is important so we beat Quill’s own paste handlers
        quillInstance.root.addEventListener("paste", handler, true);

        removePasteListener = () => {
            quillInstance?.root?.removeEventListener("paste", handler, true);
            removePasteListener = null;
        };
    };

    const toQuillHtml = (brHtml = "") => {
        let s = (brHtml ?? "").toString().replace(/[\r\n]/g, "");
        if (!s) return "";

        // Normalize all <br>, <br/>, <br /> to <br/>
        s = s.replace(/<br\s*\/?>/gi, "<br/>");

        // Use a placeholder so the next replace won't touch it
        const DBR = "__DBR__";

        // Mark DOUBLE breaks first
        s = s.replace(/<br\/>\s*<br\/>/gi, DBR);

        // Convert remaining SINGLE breaks to paragraph boundaries
        s = s.replace(/<br\/>/gi, "</p><p>");

        // Convert placeholder into an empty paragraph between paragraphs
        s = s.replaceAll(DBR, "</p><p><br></p><p>");

        // Wrap into Quill-friendly block HTML
        return `<p>${s}</p>`;
    };

    onMount(() => {
        quillInstance = new Quill(editor, {
            modules: {
                toolbar: runningConfig.showToolbar
                    ? runningConfig.toolbarOptions
                    : null,
            },
            placeholder:
                content || runningConfig.isDisabled
                    ? ""
                    : runningConfig.placeholder,
            theme: "snow",
        });

        installPlainPasteHandler();

        quillInstance.on("text-change", (delta, oldDelta, source) => {
            if (source !== "user") return;

            const html = quillInstance.getSemanticHTML();
            if ((html === "<p><br></p>" || html === "<p></p>")) {
                content = "";
                return;
            }

            content = html
                .replace(/<p><br><\/p>/g, "<br/>")
                .replace(/<\/p><p>/g, "<br/>")
                .replace(/^<p>/, "")
                .replace(/<\/p>$/, "")
                .replace(/<br\s*>/g, "<br/>")
                .replace(/&nbsp;|\u00A0/g, " ");
        });

        const initialHtml = toQuillHtml(content);
        quillInstance.clipboard.dangerouslyPasteHTML(initialHtml, "silent");
    });

    onDestroy(() => {
        removePasteListener?.();
    });

    export const setText = (text = "") => {
        if (!quillInstance) return;
        quillInstance.setText(text);
    };

    export const setContent = (content = "") => {
        if (!quillInstance) return;
        quillInstance.root.innerHTML = content;
    };

    export const focus = () => {
        if (quillInstance) {
            quillInstance.focus();
            const length = quillInstance.getLength();
            quillInstance.setSelection(length - 1, 0);
        }
    };

    const syncEditorStyle = (runningConfig) => {
        const { isDisabled, placeholder, style } = runningConfig;

        quillInstance.enable(!isDisabled);

        if (isDisabled) quillInstance.placeholder = "";
        else quillInstance.placeholder = placeholder;

        const toolbarElements = editorContainer.querySelectorAll(".ql-toolbar");
        if (toolbarElements)
            toolbarElements.forEach((toolbar) => {
                if (isDisabled) toolbar.classList.add("hidden");
                else toolbar.classList.remove("hidden");
            });

        const containerElements =
            editorContainer.querySelectorAll(".ql-container");
        if (containerElements)
            containerElements.forEach((container) => {
                if (isDisabled)
                    container.style.setProperty(
                        "border-top",
                        "1px solid #ccc",
                        "important",
                    );
                else container.style.removeProperty("border-top");
            });

        if (style && containerElements)
            containerElements.forEach((container) => {
                Object.keys(style).forEach((key) =>
                    overwriteStyle(container, key, style[key]),
                );
            });
    };

    const overwriteStyle = (element, styleName, value) => {
        const kebabCaseName = styleName.replace(
            /[A-Z]/g,
            (match) => `-${match.toLowerCase()}`,
        );

        element.style.setProperty(kebabCaseName, value, "important");
    };
</script>

<div class="flex flex-1 flex-col w-full" bind:this={editorContainer}>
    <div bind:this={editor} />
</div>

<style>
    :global(.ql-container) {
        min-height: 120px;
    }
</style>
